import { useMutation, useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";

import { useTranslation } from "@/core/i18n";
import { usePlatform } from "@/hooks/usePlatform";
import {
  canStartAgelessAssessment,
  mapAgelessFlowTypeToAssessmentFlowType,
  redirectToAgelessAssessment,
  startAgelessAssessmentForCurrentUser,
  type StartAgelessAssessmentVariables,
} from "@/modules/ageless/startAgelessAssessment/startAgelessAssessment";
import {
  type AgelessCapabilities,
  useAgelessCapabilities,
} from "@/modules/ageless/useAgelessCapabilities/useAgelessCapabilities";
import {
  getAgelessAssessmentsByAssessmentId,
  getAgelessAssessmentsByAssessmentIdRecommendations,
} from "api/methods";
import type { Models } from "api/types";
import { AgelessFooter } from "atoms/ageless-footer/AgelessFooter";
import { AgelessPageShell } from "atoms/ageless-page-shell/AgelessPageShell";
import { Button } from "atoms/button/Button";

import { AgingReportSection } from "./components/AgingReportSection";
import { ResultsHero } from "./components/ResultsHero";
import { RevisitTiles } from "./components/RevisitTiles";
import { TreatmentPlanSection } from "./components/TreatmentPlanSection";

export const AssessmentResultsPage = () => {
  const { assessmentId } = useParams<{ assessmentId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { platform } = usePlatform();
  const { capabilities: agelessCapabilities, isLoading: isAgelessCapabilitiesLoading } =
    useAgelessCapabilities();

  const {
    data: assessment,
    isLoading,
    isError,
  } = useQuery({
    ...getAgelessAssessmentsByAssessmentId.keyed({ assessmentId: assessmentId! }),
    enabled: !!assessmentId,
  });

  const consumerHealthConsentRevoked = Boolean(assessment?.consumerHealthConsentRevokedAt);

  const { data: recommendations } = useQuery({
    ...getAgelessAssessmentsByAssessmentIdRecommendations.keyed({ assessmentId: assessmentId! }),
    enabled: !!assessmentId && !!assessment && !consumerHealthConsentRevoked,
  });

  const { mutate: startAgelessAssessment, isLoading: isStartingAgelessAssessment } = useMutation({
    mutationFn: ({ capabilities, flowTypeOverride }: StartAgelessAssessmentVariables) =>
      startAgelessAssessmentForCurrentUser({ capabilities, flowTypeOverride }),
    onSuccess: ({ redirectUrl }) => redirectToAgelessAssessment(redirectUrl),
  });

  const startOverFlowType =
    assessment && canStartAgelessAssessment(agelessCapabilities)
      ? getAssessmentFlowTypeForStartOver({
          assessment,
          capabilities: agelessCapabilities,
        })
      : null;
  const canStartOver = canStartAgelessAssessment(agelessCapabilities) && startOverFlowType !== null;

  const handleStartOverClick = () => {
    if (!canStartAgelessAssessment(agelessCapabilities) || startOverFlowType === null) {
      return;
    }

    startAgelessAssessment({
      capabilities: agelessCapabilities,
      flowTypeOverride: startOverFlowType,
    });
  };

  const renderContent = () => {
    if (!assessmentId) {
      return (
        <div className="flex flex-1 items-center justify-center">
          <div className="text-secondary">{t("assessmentResults.invalidLink")}</div>
        </div>
      );
    }

    if (isLoading || (!assessment && !isError)) {
      return (
        <div className="flex flex-1 items-center justify-center">
          <div className="text-secondary">{t("common.loading")}</div>
        </div>
      );
    }

    if (isError || !assessment) {
      return (
        <div className="flex flex-1 items-center justify-center">
          <div className="text-secondary">{t("assessmentResults.notFound")}</div>
        </div>
      );
    }

    const biometricConsentRevoked = Boolean(assessment.biometricConsentRevokedAt);
    const hasAestheticData = Boolean(assessment.enhancedPhotoUrl);
    const isWellnessOnly = assessment.isWellness && !hasAestheticData;
    const flowLabel = assessment.isWellness
      ? hasAestheticData
        ? t("assessmentResults.flowAestheticWellness")
        : t("assessmentResults.flowWellness")
      : t("assessmentResults.flowAesthetic");
    const completedDate = dayjs(assessment.createdAt).format("MMMM D, YYYY");
    const headerSegments = [
      flowLabel,
      assessment.isProviderConsult ? t("assessmentResults.inOfficeConsultation") : null,
      assessment.isProviderEdited ? t("assessmentResults.reviewedByProvider") : null,
      completedDate,
    ].filter(Boolean);

    const showAnalysisScores = !consumerHealthConsentRevoked;
    const baseScore = Math.round(Number(assessment.harmonyScore ?? 0));
    const potentialScore = Math.round(Number(assessment.potentialScore ?? 0));
    const skinAge = Number(assessment.skinAge ?? 0);
    const potentialSkinAge = Number(assessment.potentialSkinAge ?? skinAge);
    const skinAgeImprovement = Math.max(0, skinAge - potentialSkinAge);
    const hasSkinAgeData = showAnalysisScores && skinAge > 0 && potentialSkinAge > 0;
    const hasResultsHeroImage = Boolean(
      assessment.frontFaceImageUrl ||
      assessment.enhancedPhotoUrl ||
      assessment.wellnessCombinedImageUrl,
    );
    const shouldRenderResultsHero = !consumerHealthConsentRevoked || hasResultsHeroImage;

    const aestheticTreatments = (recommendations?.aestheticRecommendations ?? []).map((r) => ({
      name: r.name,
      reason: r.reason,
    }));

    const wellnessTreatments = (recommendations?.wellnessRecommendations ?? []).map((r) => ({
      name: r.name,
      reason: r.reason,
      description: r.description ?? undefined,
    }));

    const hasAestheticTreatments = aestheticTreatments.length > 0;
    const hasWellnessTreatments = wellnessTreatments.length > 0;
    const isCombined = hasAestheticTreatments && hasWellnessTreatments;

    return (
      <div className="flex flex-1 flex-col overflow-y-auto">
        <div className="flex flex-col gap-6 px-4 py-4">
          <div className="text-center text-xs text-secondary">{headerSegments.join(" · ")}</div>

          {(biometricConsentRevoked || consumerHealthConsentRevoked) && (
            <ConsentRevocationNotice
              biometricConsentRevoked={biometricConsentRevoked}
              consumerHealthConsentRevoked={consumerHealthConsentRevoked}
            />
          )}

          {shouldRenderResultsHero ? (
            <ResultsHero
              beforeImage={assessment.frontFaceImageUrl}
              afterImage={assessment.enhancedPhotoUrl}
              baseScore={baseScore}
              potentialScore={potentialScore}
              skinAge={skinAge}
              potentialSkinAge={potentialSkinAge}
              skinAgeImprovement={skinAgeImprovement}
              hasSkinAgeData={hasSkinAgeData}
              showAnalysisScores={showAnalysisScores}
              firstName={assessment.firstName}
              isWellness={isWellnessOnly}
              wellnessImageUrl={assessment.wellnessCombinedImageUrl}
            />
          ) : null}

          {!consumerHealthConsentRevoked && assessment.overallRecommendation ? (
            <p className="text-center text-xs leading-relaxed text-secondary">
              {assessment.overallRecommendation}
            </p>
          ) : null}

          {!consumerHealthConsentRevoked && assessment.primaryConcerns.length > 0 && (
            <div className="flex flex-col gap-2">
              <p className="text-xs uppercase tracking-wider text-secondary">
                {t("assessmentResults.primaryConcerns")}
              </p>
              <div className="flex flex-wrap gap-2">
                {assessment.primaryConcerns.map((concern) => (
                  <span
                    key={concern}
                    className="rounded-full bg-white px-3 py-1 text-body1 capitalize text-primary shadow-sm"
                  >
                    {concern}
                  </span>
                ))}
              </div>
            </div>
          )}

          {!consumerHealthConsentRevoked && hasAestheticTreatments && (
            <TreatmentPlanSection
              title={
                isCombined
                  ? t("assessmentResults.faceTreatments")
                  : t("assessmentResults.yourTreatmentPlan")
              }
              treatments={aestheticTreatments}
            />
          )}

          {!consumerHealthConsentRevoked && hasWellnessTreatments && (
            <TreatmentPlanSection
              title={
                isCombined
                  ? t("assessmentResults.bodyTreatments")
                  : t("assessmentResults.yourTreatmentPlan")
              }
              treatments={wellnessTreatments}
            />
          )}

          {!consumerHealthConsentRevoked && (hasAestheticTreatments || hasWellnessTreatments) && (
            <div className="flex flex-col items-center gap-2 rounded-xl bg-white p-5 shadow-sm">
              <p className="text-sub2 font-semibold text-primary">
                {t("assessmentResults.getThisLook")}
              </p>
              <p className="text-center text-xs leading-relaxed text-secondary">
                {t("assessmentResults.browseTreatmentsBody", {
                  provider: platform?.name ?? t("assessmentResults.yourProvider"),
                })}
              </p>
              <button
                className="mt-1 w-full rounded-full px-6 py-3 text-body1 font-semibold text-white"
                type="button"
                style={{ backgroundColor: "rgb(var(--repeatmd-brand-color))" }}
                onClick={() => navigate("/shop/treatments")}
              >
                {t("assessmentResults.browseTreatments")}
              </button>
            </div>
          )}

          <RevisitTiles
            frontFaceImageUrl={assessment.frontFaceImageUrl}
            enhancedPhotoUrl={assessment.enhancedPhotoUrl}
            profileFaceImageUrl={assessment.profileFaceImageUrl}
            enhancedProfilePhotoUrl={assessment.enhancedProfilePhotoUrl}
            futureSelfWithoutTreatmentImageUrl={
              consumerHealthConsentRevoked ? null : assessment.futureSelfWithoutTreatmentImageUrl
            }
            futureSelfWithTreatmentImageUrl={
              consumerHealthConsentRevoked ? null : assessment.futureSelfWithTreatmentImageUrl
            }
            wellnessOriginalImageUrl={assessment.wellnessOriginalImageUrl}
            wellnessCombinedImageUrl={assessment.wellnessCombinedImageUrl}
          />

          {!consumerHealthConsentRevoked && assessment.agingReport ? (
            <AgingReportSection agingReport={assessment.agingReport} />
          ) : null}

          <p className="text-center text-[10px] leading-relaxed text-secondary">
            {t("assessmentResults.disclaimer")}
          </p>

          <Button
            fullWidth
            size="large"
            disabled={isAgelessCapabilitiesLoading || isStartingAgelessAssessment || !canStartOver}
            onClick={handleStartOverClick}
          >
            {isStartingAgelessAssessment
              ? t("assessmentResults.starting")
              : t("assessmentResults.startOver")}
          </Button>
        </div>

        <div className="grow" />
        <AgelessFooter />
      </div>
    );
  };

  return (
    <AgelessPageShell
      title={t("assessmentResults.header")}
      onBack={() => navigate("/account/assessments")}
    >
      {renderContent()}
    </AgelessPageShell>
  );
};

function ConsentRevocationNotice({
  biometricConsentRevoked,
  consumerHealthConsentRevoked,
}: {
  readonly biometricConsentRevoked: boolean;
  readonly consumerHealthConsentRevoked: boolean;
}) {
  const { t } = useTranslation();
  let title = t("assessmentResults.consentBiometricTitle");
  let message = t("assessmentResults.consentBiometricMessage");

  if (biometricConsentRevoked && consumerHealthConsentRevoked) {
    title = t("assessmentResults.consentBothTitle");
    message = t("assessmentResults.consentBothMessage");
  } else if (consumerHealthConsentRevoked) {
    title = t("assessmentResults.consentHealthTitle");
    message = t("assessmentResults.consentHealthMessage");
  }

  return (
    <div
      role="status"
      className="flex flex-col gap-1 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-center"
    >
      <p className="text-sub2 font-semibold text-primary">{title}</p>
      <p className="text-xs leading-relaxed text-secondary">{message}</p>
    </div>
  );
}

function getAssessmentFlowTypeForStartOver({
  assessment,
  capabilities,
}: {
  assessment: {
    isWellness?: boolean | null;
    enhancedPhotoUrl?: string | null;
  };
  capabilities: AgelessCapabilities;
}): Models<"AgelessAssessmentFlowType"> {
  if (typeof assessment.isWellness !== "boolean") {
    return mapAgelessFlowTypeToAssessmentFlowType(capabilities.flowType);
  }

  const hasAestheticData = Boolean(assessment.enhancedPhotoUrl);
  const assessmentFlowType: Models<"AgelessAssessmentFlowType"> = assessment.isWellness
    ? hasAestheticData
      ? "AestheticsWellness"
      : "Wellness"
    : "Aesthetics";

  if (isFlowTypeAvailableInCapabilities(assessmentFlowType, capabilities)) {
    return assessmentFlowType;
  }

  return mapAgelessFlowTypeToAssessmentFlowType(capabilities.flowType);
}

function isFlowTypeAvailableInCapabilities(
  flowType: Models<"AgelessAssessmentFlowType">,
  capabilities: AgelessCapabilities,
): boolean {
  return mapAgelessFlowTypeToAssessmentFlowType(capabilities.flowType) === flowType;
}
